import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { Ort } from '../../../model/ort';
import { OrtService } from '../../suchseite/ort.service';
import { BasisFacetteService } from './basis-facette.service';
import { RegionenService } from './regionen.service';

import { orderedRegionen } from '../../../model/regionenliste';
import { SelectedSuggestion, Suggestion } from '../../../model/suggestions';
import { isNumeric } from '../../../shared/utils/isNumeric';
/*
 Dieser Service dient dazu die OrtFacette Anzeigeproblem vorübergehend zu lösen,
 da der Suchschlitz für Ort aus der Suchleiste in die Facette verschoben wurde.
 */
@Injectable({
  providedIn: 'root'
})
export class OrtFacettenService implements BasisFacetteService {
  private readonly ortService = inject(OrtService);
  private readonly regionenService = inject(RegionenService);

  protected _expanded$ = new BehaviorSubject<boolean>(false);
  public expanded$ = this._expanded$.asObservable();
  private readonly selectedOrt = toSignal(this.ortService.ort$);
  private readonly selectedUmkreis = toSignal(this.ortService.umkreis$);

  public ortLabel$ = combineLatest([
    this.ortService.ort$,
    this.regionenService
      .getSelectItems$()
      .pipe(map((items) => items.find(({ selected }) => selected)))
  ]).pipe(
    map(([ort, selectedBundesland]) => {
      let label = '';
      if (ort) {
        const postleitzahl = `${ort?.postleitzahl ? ort.postleitzahl + ' ' : ''}`;
        label = `${postleitzahl}${ort.name}`;
      } else if (selectedBundesland) {
        return selectedBundesland.label;
      }
      return label;
    })
  );

  /**
   * Anzahl der ausgewählten, aktiven Optionen
   */
  public getNumberSelected$(): Observable<number> {
    return combineLatest([this.ortService.ort$, this.regionenService.getNumberSelected$()]).pipe(
      map(([ortOrNull, anzahlSelectedRegionen]) => (ortOrNull ? 1 : 0) + anzahlSelectedRegionen)
    );
  }

  public isOrtSelected$: Observable<boolean> = this.ortService.ort$.pipe(
    map((ortOrNull) => !!ortOrNull)
  );

  // Setzt den expansion state der Facette
  public setExpanded(expanded: boolean): void {
    this._expanded$.next(expanded);
  }

  public reset() {
    this.ortService.update(null);
    this.regionenService.reset();
  }

  public async onSuggestionSelected(selectedSuggestion: SelectedSuggestion | null) {
    if (selectedSuggestion) {
      if (selectedSuggestion.extra?.isRegion) {
        this.ortService.update(null); //reset ort
        this.regionenService.updateSelectedIds([selectedSuggestion.value?.id]);
      } else {
        const isPlzSearch = isNumeric(selectedSuggestion.input.substring(0, 6));

        if (!isPlzSearch) {
          (selectedSuggestion.value as Ort).postleitzahl = undefined;
        }
        this.regionenService.reset();
        this.setOrt(selectedSuggestion.value);
      }
    } else {
      this.reset();
    }
  }

  public loadSuggestions(searchText: string): Observable<Suggestion[]> {
    return this.ortService.getOrtBySuchwort(searchText).pipe(
      map((orte) => {
        const isPlzSearch = isNumeric(searchText);
        const ortSuggestions = orte.map((ort) =>
          Suggestion.of(isPlzSearch ? `${ort.postleitzahl} ${ort.name}` : ort.name, ort)
        );
        const bundeslandSuggestions = orderedRegionen
          .filter((region) => region.bezeichnung.toLowerCase().startsWith(searchText.toLowerCase()))
          .map((region) =>
            Suggestion.of(region.bezeichnung, { ...region }, '', { isRegion: true })
          );
        return [...bundeslandSuggestions, ...ortSuggestions];
      })
    );
  }

  public async setzePlz(plz: string) {
    await this.ortService.setzePlz(plz);
  }

  public setOrt(ort: Ort) {
    //TODO: set private and use onSuggestionSelected instead
    let umkreis = this.selectedUmkreis() ?? undefined;
    const previousOrt = this.selectedOrt();
    const isInitialInput = !previousOrt;
    if (isInitialInput) {
      // Default-Umkreis ist abhängig von der Genauigkeit der Ortseingabe
      const isPostleitzahlGenau = ort?.postleitzahl !== undefined;
      umkreis = isPostleitzahlGenau
        ? OrtService.suchumkreisRange[2] // 10 km
        : OrtService.suchumkreisRange[0]; // ganzer Ort
    }
    this.ortService.update(ort, umkreis);
  }

  public setOrtsunabhaenig(ortsunabhaengig: boolean) {
    this.ortService.updateOnlyOrtsunabhaengig(ortsunabhaengig);
  }
}
